// @flow
import React, {type Node, Fragment} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import Container from 'components/SideBar/ui/Container'
import SettingsSection from './SettingsSection'
import UserSection from './UserSection'
import Search from './Search'
import LocaleSwitcher from './LocaleSwitcher'
import UpgradeLink from './UpgradeLink'
import OrganizationSection from './OrganizationSection'
import TensionProcessing from './TensionProcessing'
import YourRolesSection from './YourRolesSection'
import YourOrganizationSection from './YourOrganizationSection'
import YourProfileAndSettings from './YourProfileAndSettings'
import SignoutButton from './SignoutButton'
import AdminSection from './AdminSection'
import ImpersonationStopper from './ImpersonationStopper'
import OrgSelect from './OrgSelect'
import type {
  SidebarView_organization as Organization,
} from './__generated__/SidebarView_organization.graphql'
import styles from './index.scss'

type Props = $ReadOnly<{
  organization: Organization,
  searchAutofocus: boolean,
  isOpen: boolean,
  toggleSidebar: () => void,
}>

declare var gf: Object

function SidebarView({
  organization,
  searchAutofocus,
  isOpen,
  toggleSidebar,
}: Props): Node {
  const viewer = organization.viewer
  const viewerIsMember = organization.viewerIsMember
  const viewerIsAdmin = organization.viewerIsAdmin
  const viewerIsSuperAdmin = viewerIsMember === false && viewerIsAdmin === true

  const renderUncollapsed = () => (
    <Fragment>
      <div className={styles.mainSection}>
        <div className={styles.userSection}>
          <UserSection
            organization={organization}
            viewer={viewer}
          />
        </div>
        {viewer && <LocaleSwitcher viewer={viewer} />}
        {organization && <UpgradeLink organization={organization} />}
        <div id="sjs-sidebar_search">
          <Search
            viewer={viewer}
            autofocus={searchAutofocus}
          />
        </div>
      </div>
      <div className={styles.regularSection}>
        {viewerIsMember && (
          <TensionProcessing organization={organization} />
        )}
        {!viewerIsMember && (
          <OrganizationSection organization={organization} />
        )}
        <hr className={styles.mainDivider} />
        {viewerIsMember && (
          <YourRolesSection organization={organization} />
        )}
        {viewerIsMember && (
          <YourOrganizationSection
            organization={organization}
            viewer={organization.viewer}
          />
        )}
        {viewerIsAdmin && (
          <AdminSection organization={organization} />
        )}
        <hr className={styles.secondaryDivider} />
        <ul className={styles.noTitleSection}>
          {viewerIsMember && <YourProfileAndSettings viewer={organization.viewer} />}
          {gf.app.true_current_person_id && <ImpersonationStopper />}
          <SignoutButton viewer={viewer} />
        </ul>
      </div>
    </Fragment>
  )

  return (
    <Container
      homePath={viewer.routes.homepagePath}
      exceedsFreePlan={organization.exceedsFreePlan && !viewerIsSuperAdmin}
      isOpen={isOpen}
      toggleSidebar={toggleSidebar}
      renderUncollapsed={renderUncollapsed}
      renderSettingsSection={() => (
        <SettingsSection
          viewer={viewer}
          organization={organization}
        />
      )}
      renderOrgSelect={() => (
        <OrgSelect
          organization={organization}
          viewer={viewer}
          sideBarExpanded={isOpen}
          toggleSidebar={toggleSidebar}
        />
      )}
    />
  )
}

export default (createFragmentContainer(SidebarView, {
  organization: graphql`
    fragment SidebarView_organization on Organization {
      exceedsFreePlan
      viewerIsMember
      viewerIsAdmin

      viewer {
        routes {
          homepagePath
        }

        ...LocaleSwitcher_viewer
        ...Search_viewer
        ...UserSection_viewer
        ...SettingsSection_viewer
        ...YourOrganizationSection_viewer
        ...YourProfileAndSettings_viewer
        ...SignoutButton_viewer
        ...OrgSelect_viewer
      }

      ...UpgradeLink_organization
      ...OrganizationSection_organization
      ...YourRolesSection_organization
      ...UserSection_organization
      ...TensionProcessing_organization
      ...SettingsSection_organization
      ...YourOrganizationSection_organization
      ...AdminSection_organization
      ...OrgSelect_organization
    }
  `,
}): RelayFragmentContainer<typeof(SidebarView)>)
